import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import {useDispatch} from 'react-redux';
import {useLawAgentsContext} from '../context/LawAgentsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FiltersBar from '../../_common/components/FiltersBar';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/lawActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {lawAgentsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersLawAgents} from '../../_common/constants/exportedFilesHeaders';
import type {LawAgentsFilterValues, LawAgentExcelItem} from "../types";

type QueryParams = Record<string, any>

const prepareFilter = (queryParams: QueryParams, values: LawAgentsFilterValues): QueryParams => {
  const {agentsAgentID, agentsAgentName, agentsIdNumber, agentsEmail} = values

  const newQueryParams = { ...queryParams }
  const filter: QueryParams = {}

  if (Boolean(agentsAgentID)) {
    filter.agentsAgentID = agentsAgentID
  } else {
    delete newQueryParams.agentsAgentID
  }

  if (Boolean(agentsAgentName)) {
    filter.agentsAgentName = agentsAgentName
  } else {
    delete newQueryParams.agentsAgentName
  }

  if (Boolean(agentsIdNumber)) {
    filter.agentsIdNumber = agentsIdNumber
  } else {
    delete newQueryParams.agentsIdNumber
  }

  if (Boolean(agentsEmail)) {
    filter.agentsEmail = agentsEmail
  } else {
    delete newQueryParams.agentsEmail
  }

  return { ...newQueryParams, ...filter }
}

export function LawAgentsFilters(): JSX.Element {
  const dispatch: any = useDispatch()
  const {formatMessage} = useIntl()
  const context: any = useLawAgentsContext()
  const props = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams
    }
  }, [context])

  const applyFilter = (values: LawAgentsFilterValues): void => {
    RequestCancellationManager.stopRequests(actions.geLawAgentsListIDPrefix)

    const newQueryParams = prepareFilter(props.queryParams, values)

    if (!isEqual(newQueryParams, props.queryParams)) {
      props.setQueryParams(newQueryParams)
    }
  }

  const qs = new URLSearchParams(window.location.search)

  const agentsAgentID: string = qs.get('agentsAgentID') || '';
  const agentsAgentName: string = qs.get('agentsAgentName') || '';
  const agentsIdNumber: string = qs.get('agentsIdNumber') || '';
  const agentsEmail: string = qs.get('agentsEmail') || '';

  const [loading, setLoading] = useState<boolean>(false)

  const handleExport = (): void => {
    setLoading(true)

    dispatch(actions.getLawAgentsForExcelFile(props.queryParams))
      .then(prepareDataForExcel)
      .then((data: any) =>
        generateExcelFile(data, 'Lawsuit-agents.xlsx', lawAgentsHeader)
      )
      .catch((error: unknown) => {
        console.error(`Error: ${error}`)
        setLoading(false)
      })
      .finally(() => setLoading(false))
  }

  const prepareDataForExcel = (data: LawAgentExcelItem[]) => {
    return data.map((item, index) => {
      const {
        agentId,
        agentName,
        idNumber,
        tamalId,
        email,
        paidSoFar,
      } = item as LawAgentExcelItem
      const remainingAmount = paidSoFar ? 515 - parseFloat(paidSoFar) : '';

      return {
        [headersLawAgents[0]]: index + 1,
        [headersLawAgents[1]]: agentId,
        [headersLawAgents[2]]: agentName,
        [headersLawAgents[3]]: idNumber,
        [headersLawAgents[4]]: tamalId,
        [headersLawAgents[5]]: email,
        [headersLawAgents[6]]: 515,
        [headersLawAgents[7]]: paidSoFar,
        [headersLawAgents[8]]: remainingAmount,
      }
    })
  }

  const prefix = 'law-filter'
  const containerClassnames = 'w-250px position-relative my-1 px-3'

  return (
    <Formik<LawAgentsFilterValues>
      initialValues={{
        agentsAgentID,
        agentsAgentName,
        agentsIdNumber,
        agentsEmail,
      }}
      onSubmit={applyFilter}
      enableReinitialize
    >
      {({ values, ...formikProps }) => (
        <FiltersBar
          filters={
            <div className="d-flex flex-wrap justify-content-center align-items-center mb-0 w-100">
              <FilterTextField
                fieldName="agentsAgentID"
                type={'number'}
                placeholder={formatMessage({id: `${prefix}-agentID`, defaultMessage: 'Agent ID'})}
                value={values.agentsAgentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentsAgentName"
                placeholder={formatMessage({id: `${prefix}-agentName`, defaultMessage: 'Agent name'})}
                value={values.agentsAgentName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentsIdNumber"
                placeholder={formatMessage({id: `${prefix}-idNumber`, defaultMessage: 'ID number'})}
                value={values.agentsIdNumber}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentsEmail"
                placeholder={formatMessage({id: `${prefix}-email`, defaultMessage: 'Email'})}
                value={values.agentsEmail}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <>
              <ButtonExportToExcel exportFunc={handleExport} loading={loading}/>
            </>
        }
        />
      )}
    </Formik>
  )
}